import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "How to make your django app slow",
  "description": "",
  "published": "2012-03-05T00:00:00.000Z",
  "redirect_from": ["/blog/how-to-make-your-django-app-slow/swizec/3825"],
  "categories": "django, Integration testing, Languages, Programming, python, TestUnit, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`The task every developer fears `}<em parentName="p">{`"Hey, I sometimes get a timeout when I want to see the last gazillion days of activity in the `}<a parentName="em" {...{
          "href": "http://en.wikipedia.org/wiki/Dashboard",
          "title": "Dashboard"
        }}>{`dashboard`}</a>{`. What gives?"`}</em></p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/03/3188032449_1e8783552a_m5.jpg",
        "alt": "Nikon GP-1 GPS Unit Test",
        "title": "Nikon GP-1 GPS Unit Test"
      }}></img></p>
    <p>{`This problem started happening for a project I work on a few months ago. Through a lucky sequence of events I was able to push the problem back a couple of months ... mostly just a beefier VPS on amazon with a bit more memory.`}</p>
    <p>{`Yesterday I finally cleaned up `}<em parentName="p">{`everything`}</em>{` `}<em parentName="p">{`else`}</em>{` from pivotal tracker and was left with only that one dreaded item. That ... slowness thing. Have you ever tried speeding up the execution of something when 80% of the codebase isn't yours?`}</p>
    <h2 {...{
      "id": "context-i-need-context"
    }}>{`Context! I need context!`}</h2>
    <p>{`Let me give you a bit of context for what's going on:`}</p>
    <ul>
      <li parentName="ul">{`the project lets customers have many users`}</li>
      <li parentName="ul">{`users enter events when they do stuff`}</li>
      <li parentName="ul">{`the entries can be checked in a dashboard`}</li>
      <li parentName="ul">{`there is a date-range picker to see more than just today's events`}</li>
    </ul>
    <p>{`And that's where it all gets ever so slightly weird. When you choose anything other than `}<em parentName="p">{`today`}</em>{` things start becoming increasingly slow and sometimes certain browsers decide that it's just too slow and timeout.`}</p>
    <p>{`No, it isn't slow enough to cause the 30 second socket timeout.`}</p>
    <p>{`No, caching wouldn't really benefit this activity either.`}</p>
    <h2 {...{
      "id": "a-simple-benchmark"
    }}>{`A simple benchmark`}</h2>
    <p>{`In light of what I've learned about `}<a parentName="p" {...{
        "href": "http://swizec.com/blog/unit-testing-is-for-lazy-people/swizec/3752",
        "title": "Unit testing is for lazy people"
      }}>{`unit testing being for lazy people`}</a>{` last week, I decided to set up a `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Unit_testing",
        "title": "Unit testing"
      }}>{`unit test`}</a>{` that is actually an `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Integration_testing",
        "title": "Integration testing"
      }}>{`integration test`}</a>{` and most likely isn't either of those since it's some sort of benchmark.`}</p>
    <p>{`But it goes in `}<a parentName="p" {...{
        "href": "http://www.djangoproject.com",
        "title": "Django (Web framework)"
      }}>{`django`}</a>{`'s testing framework, the python module behind that is called unittest, so I'm calling it a unit test.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`    def test_date_range(self):
        t1 = datetime.now()
        response = self.c.post('/accounts/dashboard/',
                               {'dateFrom': (datetime.now()-timedelta(days=356)).strftime("%m/%d/%Y")})

        t2 = datetime.now()

        self.assert_((t2-t1).seconds < 2, "Too slow")
`}</code></pre>
    <p>{`See, simple test. All it needed was exporting the whole production database into a fixture so django loads it up every time the test is run and then cleans up after itself.`}</p>
    <p>{`The results are abominable! To return a dashboard with 250 rows of entries, this django app takes between `}<strong parentName="p">{`5 and 8`}</strong>{` seconds. Clearly way way too much time!`}</p>
    <h2 {...{
      "id": "the-fix"
    }}>{`The fix?`}</h2>
    <p>{`Indexes! Yes, adding an index where appropriate should speed things up `}<em parentName="p">{`immensely`}</em>{`. Right?`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/03/300px-Allison_T56_mobile_test_unit_MCAS_Futenma_19823.jpeg",
        "alt": "U.S. Marine Corps SSgt. R. McIntosh, an engine...",
        "title": "U.S. Marine Corps SSgt. R. McIntosh, an engine..."
      }}></img></p>
    <p>{`Nope.`}</p>
    <p>{`Creating indexes didn't even put a dint in that benchmark. There was more fluctuation from the effect of memory pages on mysql's performance. And anyway ... what was I thinking really, indexes helping queries with less than 1000 rows of results? Don't be silly Swizec.`}</p>
    <p>{`Now what?`}</p>
    <p>{`Added a bunch of "spent time" prints in the code and realized that even though it takes less than a second to make everything ready it then takes five seconds to render the template.`}</p>
    <p>{`Wait what! Five seconds to render the template!?`}</p>
    <p>{`Oh right ... django's querysets are lazy. That would probably explain it. The template is where those querysets get evaluated, they ping the database and that's the explanation I'm looking for. It must be!`}</p>
    <p>{`Except it wasn't. Due to the way the `}<em parentName="p">{`view`}</em>{` is written all the querysets get iterated over before the template is rendered. And they all have that magical `}<em parentName="p">{`select_related`}</em>{` so all the fields getting accessed inside the template can't possibly be triggering more db queries can it?`}</p>
    <p>{`Well ... no.`}</p>
    <p>{`The main module has a function added that returns `}<em parentName="p">{`the latest related object.`}</em>{` This function, correctly, only gets called once per rendering for every model.`}</p>
    <p>{`But the function returns a queryset.`}</p>
    <p>{`That queryset is lazy and it gets evaluated `}<em parentName="p">{`every single time`}</em>{` that related object is used, which turns out to be `}<em parentName="p"><strong parentName="em">{`30 times`}</strong></em>{` per displayed row. But can 30 queries explain the slowness in rendering? Or should it be even slower if my theory was in fact correct?`}</p>
    <p>{`I don't know, but right now I'm working on a fix to make sure that object is solidified and is not a lazy queryset before it gets pushed to the template. If it works, great ... otherwise I have no idea what I'll try next.`}</p>
    <p><em parentName="p">{`user.get_profile()`}</em>{` also gets called awfully often. Wonder how that baby works ...`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://swizec.com/blog/unit-testing-is-for-lazy-people/swizec/3752"
        }}>{`Unit testing is for lazy people`}</a>{` (swizec.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://swizec.com/blog/simple-trick-for-testing-forms-full-of-checkboxes-with-django/swizec/3098"
        }}>{`Simple trick for testing forms full of checkboxes with django`}</a>{` (swizec.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.devsigh.com/sigh/424"
        }}>{`"architect": we have big refactors to do on out architecture but first we need to unit test everything me: I'm all for finally`}</a>{` (devsigh.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/8664551/django-charfield-queryset-filter-without-escaping-the-mysql-wildcard"
        }}>{`Django charfield queryset filter without escaping the MYSQL wildcard`}</a>{` (stackoverflow.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/9065371/do-we-still-need-django-nonrel-now-that-gae-allegedly-supports-django-out-of-t"
        }}>{`Do we still need django-nonrel now that GAE (allegedly) supports Django out of the box?`}</a>{` (stackoverflow.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/8698794/retrieving-only-the-date-part-of-a-datetime-column-in-django"
        }}>{`Retrieving only the date part of a datetime column in Django`}</a>{` (stackoverflow.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://css.dzone.com/articles/15-key-resources-learn-django-0"
        }}>{`15 Key Resources to Learn Django`}</a>{` (css.dzone.com)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=fee7947c-17cc-4d31-b348-c7a8b425b12c",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      